import { textDiv } from './texts.js';

/**
 * HelpPopup - En återanvändbar popup-klass för att visa hjälptexter
 * 
 * Exempel på användning:
 * const popup = new HelpPopup({
 *   parentElement: this.cropWrapper,
 *   textKey: 'upload_disabled_helptext',
 *   defaultText: 'Please select a paper type to enable uploading.',
 *   additionalClasses: ['custom-class']
 * });
 * 
 * popup.show(targetElement); // Visa popup relativt ett element
 * popup.hide();              // Dölj popup
 * popup.toggle(targetElement); // Toggla synlighet
 */
class HelpPopup {
    popupElement = null;
    parentElement = null;
    isOpen = false;
    horizontalAlign = 'center';
    placement = 'under';
    offsetY = 8;
    offsetX = 0;

    /**
     * @param {Object} options - Konfigurationsobjekt
     * @param {HTMLElement} options.parentElement - Parent-element som popupen ska läggas till i
     * @param {string} options.textKey - Textnyckeln för att hämta text via textDiv
     * @param {string} options.defaultText - Standardtext om textKey inte ger något resultat
     * @param {Array<string>} options.additionalClasses - Extra CSS-klasser för popupen
     * @param {boolean} options.closeOnClick - Om popupen ska stängas vid klick (default: true)
     * @param {string} options.horizontalAlign - 'center', 'left', eller 'right' (default: 'center')
     * @param {string} options.placement - 'over' eller 'under' (default: 'under')
     * @param {number} options.offsetY - Y-offset från target-elementet (default: 8)
     * @param {number} options.offsetX - X-offset från target-elementets center (default: 0)
     */
    constructor({ parentElement, textKey, defaultText = '', additionalClasses = [], closeOnClick = true, horizontalAlign = 'center', placement = 'under', offsetY = 8, offsetX = 0 }) {
        this.parentElement = parentElement;
        this.horizontalAlign = horizontalAlign;
        this.placement = placement;
        this.offsetY = offsetY;
        this.offsetX = offsetX;

        if (!this.parentElement) {
            console.error('HelpPopup: parentElement is required');
            return;
        }

        // Skapa popupen med textDiv
        this.popupElement = textDiv(this.parentElement, textKey, {
            defaultText: defaultText,
            additionalClasses: ['help-popup', horizontalAlign, placement, ...additionalClasses]
        });

        if (!this.popupElement) {
            console.error(`HelpPopup: No text found for key "${textKey}" and no defaultText provided`);
            return;
        }

        // Dölj popupen initialt
        this.popupElement.style.display = 'none';

        // Lägg till klick-hanterare om closeOnClick är true
        if (closeOnClick) {
            this.popupElement.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.hide();
            });
        }
    }

    /**
     * Visa popupen relativt ett target-element
     * @param {HTMLElement} targetElement - Elementet som popupen ska visas relativt
     */
    show(targetElement) {
        if (!this.popupElement || !targetElement) return;

        const targetRect = targetElement.getBoundingClientRect();
        const parentRect = this.parentElement.getBoundingClientRect();

        // Beräkna X-position baserat på alignment
        let leftPos = null, rightPos = null;
        switch (this.horizontalAlign) {
            case 'left':
                leftPos = (targetRect.left - parentRect.left + this.offsetX);
                break;
            case 'right':
                rightPos = (parentRect.right - targetRect.right + this.offsetX);
                break;
            case 'center':
            default:
                leftPos = ((targetRect.left - parentRect.left) + (targetRect.width / 2) + this.offsetX);
                break;
        }
        if (leftPos !== null) {
            this.popupElement.style.left = leftPos + 'px';
        } else if (rightPos !== null) {
            this.popupElement.style.right = rightPos + 'px';
        }

        this.popupElement.style.display = 'block';
        this.popupElement.style.opacity = 0;

        // Beräkna Y-position (under target-elementet)
        if (this.placement === 'under') {
            this.popupElement.style.top = (targetRect.bottom - parentRect.top + this.offsetY) + 'px';
        } else {
            // Placera över target-elementet
            this.popupElement.style.top = (targetRect.top - parentRect.top - this.popupElement.offsetHeight - this.offsetY) + 'px';
        }

        // Enkel fade-in effekt
        setTimeout(() => {
            if (this.popupElement) {
                this.popupElement.style.opacity = 1;
            }
        }, 10);
        this.isOpen = true;
    }

    /**
     * Dölj popupen
     */
    hide() {
        if (!this.popupElement) return;
        this.popupElement.style.display = 'none';
        this.popupElement.style.opacity = 0;
        this.isOpen = false;
    }

    /**
     * Toggla popupens synlighet
     * @param {HTMLElement} targetElement - Elementet som popupen ska visas relativt (krävs om popupen ska visas)
     */
    toggle(targetElement) {
        if (this.isOpen) {
            this.hide();
        } else {
            this.show(targetElement);
        }
    }

    /**
     * Ta bort popupen från DOM
     */
    destroy() {
        if (this.popupElement && this.popupElement.parentNode) {
            this.popupElement.parentNode.removeChild(this.popupElement);
        }
        this.popupElement = null;
        this.isOpen = false;
    }

    /**
     * Kontrollera om popupen är öppen
     * @returns {boolean}
     */
    get isVisible() {
        return this.isOpen;
    }
}

export default HelpPopup;
